// Sentry initialization and error monitoring utilities
// UPDATED: Refined error filtering to capture actionable errors (CORS, server errors)
//          while still ignoring non-actionable connectivity issues (offline, aborts).
//          Previously, all "Failed to fetch" errors were silently dropped, which masked
//          CORS misconfigurations and other real production issues.
//          - Tightened "cancelled" filter to a whitelist of known abort/cancel phrases
//            so partial substring matches no longer over-filter legitimate errors.
//          - Extended beforeSend to check isNonActionableError against event.message
//            and event.exception values when hint.originalException is not an Error,
//            closing the gap where message-only Sentry events bypassed the filter.

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

let sentryInitialized = false;

function getSafeUrl(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null;
  try {
    const u = new URL(rawUrl);
    return `${u.origin}${u.pathname}`;
  } catch {
    return null;
  }
}

function isUserOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

const CANCEL_PHRASES = [
  'request cancelled',
  'cancelled by user',
  'cancelled by client',
  'operation cancelled',
  'the fetch was cancelled',
];

/**
 * Returns true if the error is non-actionable and should NOT be sent to Sentry.
 *
 * Rules:
 * - Always drop: browser noise (ResizeObserver, extension artifacts, user aborts)
 * - Drop fetch/network errors ONLY when the user is actually offline.
 *   When online, a "Failed to fetch" likely indicates a real server issue (CORS
 *   misconfiguration, Edge Function crash, etc.) that warrants investigation.
 */
function isNonActionableError(message: string): boolean {
  const lower = message.toLowerCase();

  if (
    message.includes('ResizeObserver loop') ||
    message.includes('Non-Error promise rejection') ||
    message.includes('Object Not Found Matching Id') ||
    message.includes('AbortError') ||
    message.includes('The operation was aborted') ||
    CANCEL_PHRASES.some(phrase => lower.includes(phrase))
  ) {
    return true;
  }

  const isFetchOrNetworkError =
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('Load failed');

  if (isFetchOrNetworkError) {
    return isUserOffline();
  }

  return false;
}

export function initSentry(): void {
  if (!SENTRY_DSN) return;
  if (sentryInitialized) return;
  sentryInitialized = true;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    tracePropagationTargets: ['yyhljkkdktrqkktixjtk.supabase.co'],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        mask: ['input[type="password"]'],
      }),
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: false,
      }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      const error = hint?.originalException;

      if (error instanceof Error) {
        const message = error.message || '';
        if (isNonActionableError(message)) {
          return null;
        }
      } else {
        const fallbackMessage =
          event.exception?.values?.[0]?.value ||
          event.message ||
          '';
        if (fallbackMessage && isNonActionableError(fallbackMessage)) {
          return null;
        }
      }

      if (event.exception?.values) {
        const isExtensionError = event.exception.values.some(ex =>
          ex.stacktrace?.frames?.some(frame =>
            frame.filename?.includes('chrome-extension://') ||
            frame.filename?.includes('moz-extension://')
          )
        );
        if (isExtensionError) return null;
      }

      const errorValue = event.exception?.values?.[0];
      const errorMessage = errorValue?.value || event.message || '';
      const errorType = errorValue?.type || null;
      const rawUrl = event.request?.url ?? (typeof window !== 'undefined' ? window.location.href : null);
      const safeUrl = getSafeUrl(rawUrl);

      void (async () => {
        try {
          const { data } = await supabase.auth.getUser();
          const userId = data?.user?.id ?? null;
          await supabase.from('error_logs').insert({
            user_id: userId,
            sentry_event_id: event.event_id ?? null,
            error_message: errorMessage.slice(0, 500),
            error_type: errorType,
            url: safeUrl,
            metadata: event.tags ? { tags: event.tags } : null,
          });
        } catch (e) {
          console.error('[sentry] Failed to write error_log to Supabase', e);
        }
      })();

      return event;
    },
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;

    if (!reason) return;

    const message = reason?.message || String(reason);
    if (isNonActionableError(message)) {
      return;
    }

    Sentry.captureException(reason instanceof Error ? reason : new Error(message), {
      extra: { type: 'unhandledrejection' },
    });
  });
}

export function setSentryUser(user: { id: string; username: string; role: string } | null): void {
  if (!SENTRY_DSN) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      username: user.username,
      role: user.role,
    });
  } else {
    Sentry.setUser(null);
  }
}

export function addBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: 'info',
  });
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!SENTRY_DSN) return;

  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export async function withSpan<T>(
  name: string,
  op: string,
  fn: () => Promise<T>
): Promise<T> {
  if (!SENTRY_DSN) return fn();

  return Sentry.startSpan({ name, op }, () => fn());
}

export { Sentry };
